// <purpose>THE effective „Hinweise in der Liste" set (P2 Task R4, 2026-09-18), and the one place
// its precedence is decided: a clerk's STORED preference when there is one, the tenant default
// otherwise. Held in a context and fetched ONCE per session — a list paints dozens of rows, and a
// per-row read of the same preference would be dozens of requests for one answer.</purpose>
// <dependencies>GET /me/ui-preferences + PUT /me/ui-preferences/row-pills (services/api) · the
// tenant default rides in as a prop from App, which already holds /config.</dependencies>
// <constraints>A stored preference REPLACES the default as a WHOLE LIST, never merges with it
// (controller ruling, board 650688/01): a clerk who stores an empty list sees every hint, even on a
// tenant whose default hides some. That is the entire reason the wire carries `rowPills: {...} |
// null` rather than a bare list — `null` is "no preference", `[]` is a decision, and this file is
// where the two must not collapse into each other.
// THREE TIERS since 2026-10-19 (small facts on a row, operator): a clerk's stored preference → the
// tenant default → the CODE default (services/rowPillKinds DEFAULT_HIDDEN_ROW_PILLS, the kinds that
// ship hidden, e.g. the Stufe pill). Each tier replaces the one below it whole, and the same
// `null` discipline holds at both seams: core serves `uiRowPillsHidden: null` for an ABSENT config
// row (no default stated → the code default), `[]` for a present-but-blank row (a stated "hide
// nothing"), exactly as the wire carries a clerk's `rowPills: null` versus `{hidden: []}`.
// NO PROVIDER = NOTHING HIDDEN. Every surface and test double written before this preference
// existed mounts rows without the provider, and must keep behaving exactly as it did; the default
// context below is that promise, not a convenience.
// A FAILED PUT leaves the cache alone and re-throws: the settings page owns the feedback slot, and
// painting a save that did not happen is the one outcome this seam must never produce. A failed
// READ, by contrast, is silent and reads as "no stored preference" — a backend predating Task C6
// answers 404 to every clerk, and taking the list down over that would be absurd.</constraints>
import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getMyUiPreferences, putMyRowPillPreferences } from '../services/api';
import { DEFAULT_HIDDEN_ROW_PILLS } from '../services/rowPillKinds';

export interface RowPillVisibility {
  /** The EFFECTIVE hidden ids — a stored preference, or the tenant default. */
  hidden: readonly string[];
  /** The tenant default on its own — the tenant's stated list, or the code default where the tenant
   *  states none. The „Auf Vorgabe zurücksetzen" action PUTs exactly this. */
  tenantDefault: readonly string[];
  /** Whether a stored preference exists at all (i.e. `hidden` is this clerk's own list). */
  hasOwnPreference: boolean;
  /** False only while the first (and only) read is in flight. */
  settled: boolean;
  /** Whether a registry kind shows on a row. An id no kind owns is visible — see rowPillKinds. */
  isVisible: (id: string) => boolean;
  /** PUTs the whole hidden list and updates the cache. Rejects on failure, cache untouched. */
  setHidden: (ids: readonly string[]) => Promise<void>;
}

const NOTHING_HIDDEN: readonly string[] = [];

const RowPillCtx = createContext<RowPillVisibility>({
  hidden: NOTHING_HIDDEN,
  tenantDefault: NOTHING_HIDDEN,
  hasOwnPreference: false,
  settled: true,
  isVisible: () => true,
  // A no-op rather than a throw: the no-provider case is a READ posture (rows outside App's tree),
  // and a component that offers a save without a provider is a wiring bug its own tests catch.
  setHidden: async () => { /* no provider: nothing to store against */ },
});

export interface RowPillPreferenceProviderProps {
  /** `config.uiRowPillsHidden` — the tenant default, already parsed; null (or omitted) = the tenant
   *  states none and the code default applies. */
  tenantDefault?: readonly string[] | null;
  children?: ReactNode;
}

/**
 * Mounted once, in App, inside the classification provider. Written with createElement rather than
 * JSX so the hook and its provider stay in ONE `.ts` file — the precedence rule and the cache that
 * serves it are the same fact, and splitting them across two files is how one of them drifts.
 */
export const RowPillPreferenceProvider = ({ tenantDefault: statedDefault = null, children }: RowPillPreferenceProviderProps) => {
  // The tenant tier: the stated list (an empty one included — a stated "hide nothing"), or the
  // code default where the tenant states none (see the three-tier note in <constraints>).
  const tenantDefault = statedDefault ?? DEFAULT_HIDDEN_ROW_PILLS;
  // `null` is the wire's own "no stored preference" and is NOT the same as `[]` — see constraints.
  const [stored, setStored] = useState<readonly string[] | null>(null);
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    let live = true;
    getMyUiPreferences().then(prefs => {
      if (!live) return;
      setStored(prefs.rowPills === null ? null : prefs.rowPills.hidden);
      setSettled(true);
    });
    return () => { live = false; };
  }, []);

  const setHidden = useCallback(async (ids: readonly string[]) => {
    // PUT FIRST, cache second: the cache is what every row reads, so it may only move once the
    // server has accepted the list.
    await putMyRowPillPreferences([...ids]);
    setStored([...ids]);
  }, []);

  const value = useMemo<RowPillVisibility>(() => {
    const hidden = stored === null ? tenantDefault : stored;
    const hiddenSet = new Set(hidden);
    return {
      hidden,
      tenantDefault,
      hasOwnPreference: stored !== null,
      settled,
      isVisible: (id: string) => !hiddenSet.has(id),
      setHidden,
    };
  }, [stored, tenantDefault, settled, setHidden]);

  return createElement(RowPillCtx.Provider, { value }, children);
};

/** THE read. Every row signal and both settings grids consult this one accessor. */
export const useRowPillVisibility = (): RowPillVisibility => useContext(RowPillCtx);
